import { useState } from 'react'
import { Box, Button, Center, Flex, Grid, Heading, HStack, Icon, Image, Text, Tooltip, VStack } from '@chakra-ui/react'
import { BsPeople } from 'react-icons/bs'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { getAnonymousUserName } from '@/helpers/getAnonymousUserName'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { getAvatarUrl } from '@/helpers/getAvatarUrl'
import { useTranslation } from 'next-i18next/pages'

interface AffiliateUsersTableProps {
  partnerStats: any
  loading: boolean
  onUserClick: (user: any) => void
}

export const AffiliateUsersTable = ({ partnerStats, loading, onUserClick }: AffiliateUsersTableProps) => {
  const { t } = useTranslation()
  const [sortField, setSortField] = useState<
    'registeredAt' | 'lastBetAt' | 'betCount' | 'totalWagered' | 'totalReward'
  >('totalReward')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
    setCurrentPage(1)
  }

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return FaSort
    return sortOrder === 'desc' ? FaSortDown : FaSortUp
  }

  if (loading) {
    return null
  }

  const nodes = partnerStats?.getPartnerUsersStats?.nodes || []
  const hasUsers = nodes.length > 0

  // Calculate totals
  const totalUsers = partnerStats?.getPartnerUsersStats?.totalCount || 0
  const activeUsers = nodes.filter((user: any) => (user.betCount || 0) > 0).length

  // Aggregate by denomination for tooltips
  const totalByDenom: Record<string, { bet: string; amount: string }> = {}
  nodes.forEach((user: any) => {
    const byDenom = typeof user.byDenom === 'string' ? JSON.parse(user.byDenom) : user.byDenom || {}
    Object.entries(byDenom).forEach(([denomKey, denomData]: [string, any]) => {
      if (!totalByDenom[denomKey]) {
        totalByDenom[denomKey] = { bet: '0', amount: '0' }
      }
      totalByDenom[denomKey].bet = (BigInt(totalByDenom[denomKey].bet) + BigInt(denomData.bet || '0')).toString()
      totalByDenom[denomKey].amount = (
        BigInt(totalByDenom[denomKey].amount) + BigInt(denomData.amount || '0')
      ).toString()
    })
  })

  const totalWagered = nodes.reduce((sum: number, user: any) => {
    const byDenom = typeof user.byDenom === 'string' ? JSON.parse(user.byDenom) : user.byDenom || {}
    return sum + Object.values(byDenom).reduce((s: number, d: any) => s + (d.bet_usd || 0), 0)
  }, 0)
  const totalReward = nodes.reduce((sum: number, user: any) => {
    const byDenom = typeof user.byDenom === 'string' ? JSON.parse(user.byDenom) : user.byDenom || {}
    return sum + Object.values(byDenom).reduce((s: number, d: any) => s + (d.amount_usd || 0), 0)
  }, 0)

  // Prepare and sort data
  const sortedData = nodes
    .map((user: any) => {
      const byDenom = typeof user.byDenom === 'string' ? JSON.parse(user.byDenom) : user.byDenom || {}
      let totalRewardUsd = 0
      let totalBetUsd = 0
      Object.values(byDenom).forEach((denomData: any) => {
        totalRewardUsd += denomData.amount_usd || 0
        totalBetUsd += denomData.bet_usd || 0
      })
      return { ...user, _totalRewardUsd: totalRewardUsd, _totalBetUsd: totalBetUsd }
    })
    .sort((a: any, b: any) => {
      let aValue: any, bValue: any

      switch (sortField) {
        case 'registeredAt':
          aValue = a.registeredAt ? new Date(a.registeredAt).getTime() : 0
          bValue = b.registeredAt ? new Date(b.registeredAt).getTime() : 0
          break
        case 'lastBetAt':
          aValue = a.lastBetAt ? new Date(a.lastBetAt).getTime() : 0
          bValue = b.lastBetAt ? new Date(b.lastBetAt).getTime() : 0
          break
        case 'betCount':
          aValue = a.betCount || 0
          bValue = b.betCount || 0
          break
        case 'totalWagered':
          aValue = a._totalBetUsd
          bValue = b._totalBetUsd
          break
        case 'totalReward':
        default:
          aValue = a._totalRewardUsd
          bValue = b._totalRewardUsd
          break
      }

      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue
    })

  const paginatedData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const totalPages = Math.ceil(sortedData.length / itemsPerPage)
  const maxVisiblePages = 5
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1)
  }

  const pageNumbers = []
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i)
  }

  return (
    <Box>
      <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" mb={6}>
        {t('affiliate.table.title')}
      </Heading>

      <Box mb={6} rounded="2xl" overflow="hidden" bg="ninja.600" borderWidth="1px" borderColor="ninja.500">
        <Grid templateColumns={{ base: '1fr', md: '220px 1fr' }}>
          <Flex
            direction="column"
            justify="center"
            align={{ base: 'flex-start', md: 'center' }}
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            bg="ninja.700"
            borderBottomWidth={{ base: '1px', md: 0 }}
            borderRightWidth={{ base: 0, md: '1px' }}
            borderColor="ninja.500"
          >
            <Text color="brand.green" fontSize={{ base: '3xl', md: '4xl' }} fontWeight={800} lineHeight={1}>
              0.5%
            </Text>
            <Text
              mt={1}
              color="ninja.300"
              fontSize="xs"
              fontWeight={700}
              letterSpacing="wide"
              textTransform="uppercase"
            >
              {t('affiliate.table.volume-note-rate-label')}
            </Text>
          </Flex>

          <Flex direction="column" px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} gap={3}>
            <Text color="ninja.200" fontSize="xs" fontWeight={700} letterSpacing="wide" textTransform="uppercase">
              {t('affiliate.table.volume-note-title')}
            </Text>
            <Text color="ninja.100" fontSize={{ base: 'sm', md: 'md' }} fontWeight={600} lineHeight={1.5}>
              {t('affiliate.table.volume-note')}
            </Text>

            <HStack spacing={2} flexWrap="wrap">
              <Box px={3} py={1} rounded="full" bg="ninja.700" borderWidth="1px" borderColor="ninja.500">
                <Text color="ninja.100" fontSize="xs" fontWeight={700}>
                  {t('affiliate.table.volume-note-chip-volume')}
                </Text>
              </Box>
            </HStack>
          </Flex>
        </Grid>
      </Box>

      {/* Summary Stats */}
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' }} gap={4} mb={6}>
        <Box bg="ninja.600" rounded="xl" p={5}>
          <Text color="ninja.300" fontSize="xs" mb={2} textTransform="uppercase" letterSpacing="wide">
            {t('affiliate.table.total-users')}
          </Text>
          <Text fontSize="3xl" fontWeight={700} color="white">
            {totalUsers}{' '}
            {activeUsers > 0 && (
              <Text as="span" color="ninja.200" fontSize="xl">
                ({activeUsers})
              </Text>
            )}
          </Text>
        </Box>
        <Box bg="ninja.600" rounded="xl" p={5}>
          <Text color="ninja.300" fontSize="xs" mb={2} textTransform="uppercase" letterSpacing="wide">
            {t('affiliate.table.total-bet')}
          </Text>
          <Tooltip
            label={
              <VStack align="start" spacing={2} p={2}>
                {Object.entries(totalByDenom).map(([denomKey, denomData]: [string, any]) => {
                  const tokenAmount = denomData.bet || '0'
                  const denom = denomKey.replaceAll('_', '/')
                  return (
                    <HStack key={denomKey} justify="space-between" w="full" spacing={4}>
                      <FiatOrCryptoAmount
                        value={new TokenMoney(tokenAmount, denom)}
                        iconSize={4}
                        wrapperStyles={{ gap: 2 }}
                        amountStyles={{ fontSize: 'sm', fontWeight: 600, color: 'ninja.100' }}
                        currencyStyles={{ fontSize: 'sm', color: 'ninja.300' }}
                      />
                    </HStack>
                  )
                })}
              </VStack>
            }
            hasArrow
            bg="ninja.600"
            borderColor="ninja.500"
            borderWidth="1px"
            color="white"
            placement="top"
            borderRadius="xl"
            p={1}
          >
            <Text fontSize="3xl" fontWeight={700} color="white" cursor="pointer">
              ${(totalWagered / 1000000).toFixed(2)}
            </Text>
          </Tooltip>
        </Box>
        <Box bg="ninja.600" rounded="xl" p={5}>
          <Text color="ninja.300" fontSize="xs" mb={2} textTransform="uppercase" letterSpacing="wide">
            {t('affiliate.table.total-reward')}
          </Text>
          <Tooltip
            label={
              <VStack align="start" spacing={2} p={2}>
                {Object.entries(totalByDenom).map(([denomKey, denomData]: [string, any]) => {
                  const tokenAmount = denomData.amount || '0'
                  const denom = denomKey.replaceAll('_', '/')
                  return (
                    <HStack key={denomKey} justify="space-between" w="full" spacing={4}>
                      <FiatOrCryptoAmount
                        value={new TokenMoney(tokenAmount, denom)}
                        iconSize={4}
                        wrapperStyles={{ gap: 2 }}
                        amountStyles={{ fontSize: 'sm', fontWeight: 600, color: 'ninja.100' }}
                        currencyStyles={{ fontSize: 'sm', color: 'ninja.300' }}
                      />
                    </HStack>
                  )
                })}
              </VStack>
            }
            hasArrow
            bg="ninja.600"
            borderColor="ninja.500"
            borderWidth="1px"
            color="white"
            placement="top"
            borderRadius="xl"
            p={1}
          >
            <Text fontSize="3xl" fontWeight={700} color="brand.green" opacity={'0.8'} cursor="pointer">
              ${(totalReward / 1000000).toFixed(2)}
            </Text>
          </Tooltip>
        </Box>
      </Grid>

      {/* Table Header - Desktop */}
      <Box display={{ base: 'none', lg: 'block' }}>
        <Flex gap={6} align="center" px={4} py={2} mb={3}>
          <Text flex="2" fontSize="xs" fontWeight={700} color="ninja.200" textTransform="uppercase">
            {t('affiliate.table.user')}
          </Text>
          <Flex flex="1" align="center" gap={1} cursor="pointer" onClick={() => handleSort('registeredAt')}>
            <Text fontSize="xs" fontWeight={700} color="ninja.200" textTransform="uppercase">
              {t('affiliate.table.registered')}
            </Text>
            <Icon as={getSortIcon('registeredAt')} boxSize={3} color="ninja.200" />
          </Flex>
          <Flex flex="1" align="center" gap={1} cursor="pointer" onClick={() => handleSort('lastBetAt')}>
            <Text fontSize="xs" fontWeight={700} color="ninja.200" textTransform="uppercase">
              {t('affiliate.table.last-bet')}
            </Text>
            <Icon as={getSortIcon('lastBetAt')} boxSize={3} color="ninja.200" />
          </Flex>
          <Flex
            flex="1"
            align="center"
            justify="flex-end"
            gap={1}
            cursor="pointer"
            onClick={() => handleSort('betCount')}
          >
            <Text fontSize="xs" fontWeight={700} color="ninja.200" textTransform="uppercase">
              {t('affiliate.table.games')}
            </Text>
            <Icon as={getSortIcon('betCount')} boxSize={3} color="ninja.200" />
          </Flex>
          <Flex
            flex="1"
            align="center"
            justify="flex-end"
            gap={1}
            cursor="pointer"
            onClick={() => handleSort('totalWagered')}
          >
            <Text fontSize="xs" fontWeight={700} color="ninja.200" textTransform="uppercase">
              {t('affiliate.table.total-bet')}
            </Text>
            <Icon as={getSortIcon('totalWagered')} boxSize={3} color="ninja.200" />
          </Flex>
          <Flex
            flex="1"
            align="center"
            justify="flex-end"
            gap={1}
            cursor="pointer"
            onClick={() => handleSort('totalReward')}
          >
            <Text fontSize="xs" fontWeight={700} color="ninja.200" textTransform="uppercase">
              {t('affiliate.table.total-reward')}
            </Text>
            <Icon as={getSortIcon('totalReward')} boxSize={3} color="ninja.200" />
          </Flex>
        </Flex>
      </Box>

      {/* Mobile Sort Controls */}
      {hasUsers && (
        <Flex display={{ base: 'flex', lg: 'none' }} gap={2} mb={4} flexWrap="wrap">
          <Button
            size="sm"
            variant={sortField === 'totalReward' ? 'solid' : 'outline'}
            colorScheme={sortField === 'totalReward' ? 'blue' : 'gray'}
            onClick={() => handleSort('totalReward')}
            rightIcon={<Icon as={getSortIcon('totalReward')} />}
          >
            {t('affiliate.table.reward')}
          </Button>
          <Button
            size="sm"
            variant={sortField === 'totalWagered' ? 'solid' : 'outline'}
            colorScheme={sortField === 'totalWagered' ? 'blue' : 'gray'}
            onClick={() => handleSort('totalWagered')}
            rightIcon={<Icon as={getSortIcon('totalWagered')} />}
          >
            {t('affiliate.table.bet')}
          </Button>
          <Button
            size="sm"
            variant={sortField === 'betCount' ? 'solid' : 'outline'}
            colorScheme={sortField === 'betCount' ? 'blue' : 'gray'}
            onClick={() => handleSort('betCount')}
            rightIcon={<Icon as={getSortIcon('betCount')} />}
          >
            {t('affiliate.table.games')}
          </Button>
          <Button
            size="sm"
            variant={sortField === 'registeredAt' ? 'solid' : 'outline'}
            colorScheme={sortField === 'registeredAt' ? 'blue' : 'gray'}
            onClick={() => handleSort('registeredAt')}
            rightIcon={<Icon as={getSortIcon('registeredAt')} />}
          >
            {t('affiliate.table.registered')}
          </Button>
          <Button
            size="sm"
            variant={sortField === 'lastBetAt' ? 'solid' : 'outline'}
            colorScheme={sortField === 'lastBetAt' ? 'blue' : 'gray'}
            onClick={() => handleSort('lastBetAt')}
            rightIcon={<Icon as={getSortIcon('lastBetAt')} />}
          >
            {t('affiliate.table.last-bet')}
          </Button>
        </Flex>
      )}

      {/* Table Content */}
      <Box bg="ninja.600" rounded="2xl" px={{ base: 3, md: 4 }} py={{ base: 4, md: 5 }}>
        <VStack spacing={5} align="stretch">
          {!hasUsers && (
            <Center py={8} color="ninja.300" fontSize={{ base: 'sm', md: 'md' }}>
              {t('affiliate.table.empty')}
            </Center>
          )}

          {paginatedData.map((user: any, index: number, array: any[]) => {
            const byDenom = typeof user.byDenom === 'string' ? JSON.parse(user.byDenom) : user.byDenom || {}
            const totalBetUsd = user._totalBetUsd
            const totalRewardUsd = user._totalRewardUsd
            const isLastRow = index === array.length - 1

            return (
              <Box key={user.userId}>
                {/* Desktop View */}
                <Flex gap={6} align="center" display={{ base: 'none', lg: 'flex' }}>
                  {/* User Column */}
                  <Flex flex="2" align="center" gap={3}>
                    <Box
                      w="36px"
                      h="36px"
                      rounded="full"
                      overflow="hidden"
                      bg="ninja.700"
                      border="2px solid"
                      borderColor="ninja.500"
                      flexShrink={0}
                      cursor="pointer"
                      onClick={() => onUserClick(user)}
                      transition="all 0.2s"
                      _hover={{ borderColor: 'brand.purple', transform: 'scale(1.1)' }}
                    >
                      {user.profileAvatar ? (
                        <Image
                          src={getAvatarUrl(user.profileAvatar)}
                          alt={user.profileUserName || 'User'}
                          w="full"
                          h="full"
                          objectFit="cover"
                        />
                      ) : (
                        <Center h="full">
                          <Icon as={BsPeople} boxSize={4} color="ninja.300" />
                        </Center>
                      )}
                    </Box>
                    <Text
                      fontWeight={700}
                      fontSize="sm"
                      color="white"
                      cursor="pointer"
                      onClick={() => onUserClick(user)}
                      transition="color 0.2s"
                      _hover={{ color: 'brand.purple' }}
                      noOfLines={1}
                    >
                      {user.profileUserName.startsWith('inj1')
                        ? getAnonymousUserName(user.userId)
                        : user.profileUserName}
                    </Text>
                  </Flex>

                  {/* Registered Column */}
                  <Box flex="1">
                    <Text fontSize="sm" color="ninja.100" fontWeight={700}>
                      {user.registeredAt
                        ? new Date(user.registeredAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </Text>
                  </Box>

                  {/* Last Bet Column */}
                  <Box flex="1">
                    <Text
                      fontSize="sm"
                      color={(() => {
                        if (!user.lastBetAt) return 'ninja.300'
                        const oneMonthAgo = new Date()
                        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
                        return new Date(user.lastBetAt) >= oneMonthAgo ? 'brand.green' : 'brand.yellow'
                      })()}
                      opacity={'0.8'}
                      fontWeight={700}
                    >
                      {user.lastBetAt
                        ? new Date(user.lastBetAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </Text>
                  </Box>

                  {/* Games Column */}
                  <Flex flex="1" justify="flex-end">
                    <Text fontSize="sm" fontWeight={700} color="white">
                      {user.betCount || 0}
                    </Text>
                  </Flex>

                  {/* Total Wagered Column */}
                  <Flex flex="1" justify="flex-end">
                    <Tooltip
                      label={
                        <VStack align="start" spacing={2} p={2}>
                          {Object.entries(byDenom).map(([denomKey, denomData]: [string, any]) => {
                            const tokenAmount = denomData.bet || '0'
                            const denom = denomKey.replaceAll('_', '/')
                            return (
                              <HStack key={denomKey} justify="space-between" w="full" spacing={4}>
                                <FiatOrCryptoAmount
                                  value={new TokenMoney(tokenAmount, denom)}
                                  iconSize={4}
                                  wrapperStyles={{ gap: 2 }}
                                  amountStyles={{ fontSize: 'sm', fontWeight: 600, color: 'ninja.100' }}
                                  currencyStyles={{ fontSize: 'sm', color: 'ninja.300' }}
                                />
                              </HStack>
                            )
                          })}
                        </VStack>
                      }
                      hasArrow
                      bg="ninja.600"
                      borderColor="ninja.500"
                      borderWidth="1px"
                      color="white"
                      placement="left"
                      borderRadius="xl"
                      p={1}
                    >
                      <Text fontSize="sm" fontWeight={700} color="white" cursor="pointer">
                        ${(totalBetUsd / 1000000).toFixed(2)}
                      </Text>
                    </Tooltip>
                  </Flex>

                  {/* Total Reward Column */}
                  <Flex flex="1" justify="flex-end">
                    <Tooltip
                      label={
                        <VStack align="start" spacing={2} p={2}>
                          {Object.entries(byDenom).map(([denomKey, denomData]: [string, any]) => {
                            const tokenAmount = denomData.amount || '0'
                            const denom = denomKey.replaceAll('_', '/')
                            return (
                              <HStack key={denomKey} justify="space-between" w="full" spacing={4}>
                                <FiatOrCryptoAmount
                                  value={new TokenMoney(tokenAmount, denom)}
                                  iconSize={4}
                                  wrapperStyles={{ gap: 2 }}
                                  amountStyles={{ fontSize: 'sm', fontWeight: 600, color: 'ninja.100' }}
                                  currencyStyles={{ fontSize: 'sm', color: 'ninja.300' }}
                                />
                              </HStack>
                            )
                          })}
                        </VStack>
                      }
                      hasArrow
                      bg="ninja.600"
                      borderColor="ninja.500"
                      borderWidth="1px"
                      color="white"
                      placement="left"
                      borderRadius="xl"
                      p={1}
                    >
                      <Text fontSize="sm" fontWeight={700} color="brand.green" opacity={'0.8'} cursor="pointer">
                        ${(totalRewardUsd / 1000000).toFixed(2)}
                      </Text>
                    </Tooltip>
                  </Flex>
                </Flex>

                {/* Mobile View */}
                <Box display={{ base: 'block', lg: 'none' }}>
                  <VStack align="stretch" spacing={3}>
                    {/* First Row: Avatar - Name - Registered Date */}
                    <Flex gap={3} align="center" pl={4}>
                      <Box
                        w="40px"
                        h="40px"
                        rounded="full"
                        overflow="hidden"
                        bg="ninja.700"
                        border="2px solid"
                        borderColor="ninja.500"
                        flexShrink={0}
                        cursor="pointer"
                        onClick={() => onUserClick(user)}
                      >
                        {user.profileAvatar ? (
                          <Image
                            src={getAvatarUrl(user.profileAvatar)}
                            alt={user.profileUserName || 'User'}
                            w="full"
                            h="full"
                            objectFit="cover"
                          />
                        ) : (
                          <Center h="full">
                            <Icon as={BsPeople} boxSize={5} color="ninja.300" />
                          </Center>
                        )}
                      </Box>
                      <Text
                        fontWeight={700}
                        fontSize="sm"
                        color="white"
                        cursor="pointer"
                        onClick={() => onUserClick(user)}
                        noOfLines={1}
                        flex={1}
                      >
                        {user.profileUserName.startsWith('inj1')
                          ? getAnonymousUserName(user.userId)
                          : user.profileUserName}
                      </Text>
                      {user.registeredAt && (
                        <Text fontSize="xs" color="ninja.300" flexShrink={0}>
                          {new Date(user.registeredAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Text>
                      )}
                    </Flex>

                    {/* Second Row: Labels */}
                    <Grid templateColumns="repeat(4, 1fr)" gap={2}>
                      <Text fontSize="xs" color="ninja.300" textAlign="center">
                        Games
                      </Text>
                      <Text fontSize="xs" color="ninja.300" textAlign="center">
                        Bet
                      </Text>
                      <Text fontSize="xs" color="ninja.300" textAlign="center">
                        Reward
                      </Text>
                      <Text fontSize="xs" color="ninja.300" textAlign="center">
                        Last Bet
                      </Text>
                    </Grid>

                    {/* Third Row: Data */}
                    <Grid templateColumns="repeat(4, 1fr)" gap={2}>
                      <Text fontWeight={700} color="white" fontSize="sm" textAlign="center">
                        {user.betCount || 0}
                      </Text>
                      <Tooltip
                        label={
                          <VStack align="start" spacing={2} p={2}>
                            {Object.entries(byDenom).map(([denomKey, denomData]: [string, any]) => {
                              const tokenAmount = denomData.bet || '0'
                              const denom = denomKey.replaceAll('_', '/')
                              return (
                                <HStack key={denomKey} justify="space-between" w="full" spacing={4}>
                                  <FiatOrCryptoAmount
                                    value={new TokenMoney(tokenAmount, denom)}
                                    iconSize={4}
                                    wrapperStyles={{ gap: 2 }}
                                    amountStyles={{ fontSize: 'sm', fontWeight: 600, color: 'ninja.100' }}
                                    currencyStyles={{ fontSize: 'sm', color: 'ninja.300' }}
                                  />
                                </HStack>
                              )
                            })}
                          </VStack>
                        }
                        hasArrow
                        bg="ninja.600"
                        borderColor="ninja.500"
                        borderWidth="1px"
                        color="white"
                        placement="top"
                        borderRadius="xl"
                        p={1}
                      >
                        <Text fontWeight={700} color="white" cursor="pointer" fontSize="sm" textAlign="center">
                          ${(totalBetUsd / 1000000).toFixed(2)}
                        </Text>
                      </Tooltip>
                      <Tooltip
                        label={
                          <VStack align="start" spacing={2} p={2}>
                            {Object.entries(byDenom).map(([denomKey, denomData]: [string, any]) => {
                              const tokenAmount = denomData.amount || '0'
                              const denom = denomKey.replaceAll('_', '/')
                              return (
                                <HStack key={denomKey} justify="space-between" w="full" spacing={4}>
                                  <FiatOrCryptoAmount
                                    value={new TokenMoney(tokenAmount, denom)}
                                    iconSize={4}
                                    wrapperStyles={{ gap: 2 }}
                                    amountStyles={{ fontSize: 'sm', fontWeight: 600, color: 'ninja.100' }}
                                    currencyStyles={{ fontSize: 'sm', color: 'ninja.300' }}
                                  />
                                </HStack>
                              )
                            })}
                          </VStack>
                        }
                        hasArrow
                        bg="ninja.600"
                        borderColor="ninja.500"
                        borderWidth="1px"
                        color="white"
                        placement="top"
                        borderRadius="xl"
                        p={1}
                      >
                        <Text
                          fontWeight={700}
                          color="brand.green"
                          opacity="0.8"
                          cursor="pointer"
                          fontSize="sm"
                          textAlign="center"
                        >
                          ${(totalRewardUsd / 1000000).toFixed(2)}
                        </Text>
                      </Tooltip>
                      <Text
                        fontSize="xs"
                        color={(() => {
                          if (!user.lastBetAt) return 'ninja.300'
                          const oneMonthAgo = new Date()
                          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
                          return new Date(user.lastBetAt) >= oneMonthAgo ? 'brand.green' : 'orange.400'
                        })()}
                        opacity="0.8"
                        textAlign="center"
                        fontWeight={600}
                      >
                        {user.lastBetAt
                          ? new Date(user.lastBetAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </Text>
                    </Grid>
                  </VStack>
                </Box>

                {/* Divider */}
                {!isLastRow && <Box h="1px" bg="ninja.500" mt={{ base: 4, lg: 5 }} />}
              </Box>
            )
          })}
        </VStack>
      </Box>

      {/* Pagination */}
      {hasUsers && totalPages > 1 && (
        <Flex
          justify={{ base: 'center', md: 'space-between' }}
          align="center"
          mt={6}
          flexWrap="wrap"
          gap={4}
          direction={{ base: 'column', md: 'row' }}
        >
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="ninja.300" order={{ base: 2, md: 1 }}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)}{' '}
            of {sortedData.length} users
          </Text>

          <HStack spacing={{ base: 1, md: 2 }} flexWrap="wrap" justify="center" order={{ base: 1, md: 2 }}>
            <Button
              size={{ base: 'xs', md: 'sm' }}
              variant="ghost"
              color="ninja.100"
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              _hover={{ bg: 'ninja.500' }}
              rounded="xl"
              fontSize={{ base: 'xs', md: 'sm' }}
              px={{ base: 2, md: 4 }}
            >
              Previous
            </Button>

            {startPage > 1 && (
              <>
                <Button
                  size={{ base: 'xs', md: 'sm' }}
                  variant="ghost"
                  color="ninja.100"
                  onClick={() => setCurrentPage(1)}
                  _hover={{ bg: 'ninja.500' }}
                  rounded="xl"
                  minW={{ base: '28px', md: 'auto' }}
                  px={{ base: 2, md: 4 }}
                >
                  1
                </Button>
                {startPage > 2 && (
                  <Text color="ninja.200" px={{ base: 1, md: 2 }} fontSize={{ base: 'xs', md: 'sm' }}>
                    ...
                  </Text>
                )}
              </>
            )}

            {pageNumbers.map((pageNum) => (
              <Button
                key={pageNum}
                size={{ base: 'xs', md: 'sm' }}
                variant={currentPage === pageNum ? 'solid' : 'ghost'}
                bg={currentPage === pageNum ? 'brand.purple' : 'transparent'}
                color={currentPage === pageNum ? 'white' : 'ninja.100'}
                onClick={() => setCurrentPage(pageNum)}
                _hover={{ bg: currentPage === pageNum ? 'brand.purple' : 'ninja.500' }}
                rounded="xl"
                minW={{ base: '28px', md: 'auto' }}
                px={{ base: 2, md: 4 }}
                fontSize={{ base: 'xs', md: 'sm' }}
              >
                {pageNum}
              </Button>
            ))}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <Text color="ninja.200" px={{ base: 1, md: 2 }} fontSize={{ base: 'xs', md: 'sm' }}>
                    ...
                  </Text>
                )}
                <Button
                  size={{ base: 'xs', md: 'sm' }}
                  variant="ghost"
                  color="ninja.100"
                  onClick={() => setCurrentPage(totalPages)}
                  _hover={{ bg: 'ninja.500' }}
                  rounded="xl"
                  minW={{ base: '28px', md: 'auto' }}
                  px={{ base: 2, md: 4 }}
                >
                  {totalPages}
                </Button>
              </>
            )}

            <Button
              size={{ base: 'xs', md: 'sm' }}
              variant="ghost"
              color="ninja.100"
              isDisabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              _hover={{ bg: 'ninja.500' }}
              rounded="xl"
              fontSize={{ base: 'xs', md: 'sm' }}
              px={{ base: 2, md: 4 }}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </Box>
  )
}
